import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MetricCard, Card, Plate } from '../components/Card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { getDashboard, getSalesSummary, getSalesOrders } from '../api';

function fmt(n) {
  if (!n) return '0 ₽';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн ₽`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} тыс ₽`;
  return `${n.toFixed(0)} ₽`;
}

const BANK_ICONS = {
  sber: '🟢', tinkoff: '⚫', tinkoff_bob: '🟣',
  tochka: '🔵', tochka_bober: '🟠',
};
const BANK_NAMES = {
  sber: 'Сбербанк',
  tinkoff: 'Т-Банк (ИП)',
  tinkoff_bob: 'Т-Банк (ООО БОБ)',
  tochka: 'Точка (Финдиректор)',
  tochka_bober: 'Точка (ИП Бобров)',
};

export default function Overview() {
  const [dash, setDash] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    getDashboard().then(setDash).catch(() => {});
    getSalesSummary().then(setSummary).catch(() => {});
    getSalesOrders(30).then(setOrders).catch(() => {});
  }, []);

  const chartData = React.useMemo(() => {
    const byDay = {};
    orders.forEach(o => {
      const d = o.date ? o.date.slice(0, 10) : '';
      if (!d) return;
      byDay[d] = (byDay[d] || 0) + o.amount;
    });
    return Object.entries(byDay).sort().slice(-14).map(([date, revenue]) => ({
      date: date.slice(5),
      revenue,
    }));
  }, [orders]);

  return (
    <div>
      <div className="kp-hero">
        <Plate>Ключ под ключ · обзор</Plate>
        <h1>Обзор бизнеса</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
          Деньги, выручка и сделки в реальном времени
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <MetricCard title="Общий остаток" value={fmt(dash?.total_cash)} sub="Все банки" />
        <MetricCard title="Выручка (30 дней)" value={fmt(dash?.revenue_30d)} color="var(--green)" />
        <MetricCard title="Открытые сделки" value={dash?.open_deals?.count ?? '—'} sub={fmt(dash?.open_deals?.amount) + ' в работе'} />
        <MetricCard title="Дебиторка" value={fmt(dash?.debtor_total)} color="var(--yellow)" />
        <MetricCard title="ФОТ месяц" value={fmt(dash?.payroll_current_month)} />
      </div>

      <Card style={{
        marginBottom: 24,
        background: 'var(--accent)',
        border: 'none',
        color: '#fff',
        '--text2': 'rgba(255,255,255,0.88)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 5 }}>
              Partner SaaS для розницы
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.45, maxWidth: 620 }}>
              Собственный аналог «МойСклад»: склад, продажи, касса/эквайринг, royalty, подписка и автоматические ограничения при просрочке.
            </div>
          </div>
          <Link
            to="/partner-saas"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '11px 18px',
              borderRadius: 10,
              background: '#fff',
              color: 'var(--brand-dark)',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              fontSize: 13,
              fontWeight: 900,
              boxShadow: '0 8px 24px rgba(0,167,220,0.24)',
            }}
          >
            Открыть приложение
          </Link>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 24 }}>
        <Card title="Выручка за 14 дней">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <XAxis dataKey="date" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
              <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}к`} tick={{ fill: 'var(--text2)', fontSize: 11 }} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8 }} />
              <Bar dataKey="revenue" fill="var(--accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Остатки по банкам">
          {dash?.cash_by_bank?.map(b => (
            <div key={b.bank} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <span>{BANK_ICONS[b.bank] || '🏦'} {BANK_NAMES[b.bank] || b.bank}</span>
              {b.stale ? (
                <span style={{ fontWeight: 600, color: '#e08a00', whiteSpace: 'nowrap' }}>
                  нет связи · данные от {b.snapped_at ? new Date(b.snapped_at).toLocaleDateString('ru-RU') : '—'}
                </span>
              ) : (
                <span style={{ fontWeight: 600 }}>{fmt(b.balance)}</span>
              )}
            </div>
          ))}
          {!dash?.cash_by_bank?.length && <div style={{ color: 'var(--text2)' }}>Нет данных</div>}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
        {summary && ['today', 'week', 'month'].map((k, i) => (
          <Card key={k} title={['Сегодня', 'Неделя', 'Месяц'][i]}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>{fmt(summary[k]?.revenue)}</div>
            <div style={{ color: 'var(--text2)', marginTop: 4 }}>{summary[k]?.orders} заказов</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
